import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, ArrowUUpLeft } from "@phosphor-icons/react";

// Admin gate for sales-rep drafts (proposals + packets): Approve & send, or send back with a note.
export default function ReviewPanel({ kind, doc, repName, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const base = kind === "proposal" ? `/proposals/${doc.id}/review` : `/contractor-packets/${doc.id}/review`;
  const what = kind === "proposal" ? "proposal" : "onboarding packet";
  const target = kind === "proposal" ? `the client (${doc.contact_email})` : `the worker (${doc.worker_email})`;

  const act = async (action) => {
    if (action === "request_changes" && !note.trim()) { toast.error("Tell the rep what to change."); return; }
    if (action === "approve" && !window.confirm(`Approve this ${what} and send it to ${target} now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(base, { action, note: note.trim() || null });
      toast.success(action === "approve"
        ? `Approved — ${what} sent to ${target}.${data.sent === false ? " (Email provider did not confirm delivery — check Sent Emails.)" : ""}`
        : "Sent back to the rep with your note.");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };

  const changesPending = doc.status === "changes_requested";
  return (
    <div className={`border-2 rounded-md p-5 mb-4 ${changesPending ? "bg-orange-50 border-orange-400" : "bg-amber-50 border-amber-500"}`} data-testid="review-panel">
      <div className="font-black text-amber-900 text-lg">
        {changesPending ? "Waiting on the rep's changes" : `Needs your review — submitted by ${repName || "a sales rep"}`}
      </div>
      <p className="text-sm text-amber-900 mt-1">
        {changesPending
          ? <>You asked for changes{doc.reviewed_at ? ` on ${String(doc.reviewed_at).slice(0, 10)}` : ""}: <i>“{doc.review_note}”</i>. The {what} stays unsent until {repName || "the rep"} resubmits — you can still approve as-is below.</>
          : <>This {what} has <b>not</b> been sent to {target}. Approve to send it now, or send it back with a note.</>}
      </p>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="review-note"
                placeholder="Optional note to the rep (required when sending back)"
                className="mt-3 w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white" />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => act("approve")} disabled={busy} data-testid="review-approve-btn"
                className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60">
          <CheckCircle size={18} weight="bold" /> {busy ? "Working…" : "Approve & send"}
        </button>
        <button type="button" onClick={() => act("request_changes")} disabled={busy} data-testid="review-changes-btn"
                className="h-11 px-4 bg-white border border-amber-500 text-amber-900 font-bold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60">
          <ArrowUUpLeft size={18} weight="bold" /> Send back with note
        </button>
      </div>
    </div>
  );
}
